if (!customElements.get('pf-premium-slideshow')) {
  customElements.define('pf-premium-slideshow', class extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.pf-product-slide')];
      this.controls = this.querySelector('.pf-slide-controls');
      if (!this.controls) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.index = 0;
      this.paused = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.hovered = false;
      this.controls.hidden = this.slides.length < 2;
      this.querySelector('.pf-hero-arrows').hidden = this.slides.length < 2;
      this.pauseButton = this.querySelector('[data-pause]');
      this.updatePause();
      this.addEventListener('click', event => {
        const step = event.target.closest('[data-step]');
        if (step) this.show(this.index + Number(step.dataset.step));
        const dot = event.target.closest('[data-slide]');
        if (dot) this.show(Number(dot.dataset.slide));
        if (event.target.closest('[data-pause]')) {
          this.paused = !this.paused;
          this.updatePause();
        }
        this.schedule();
      }, options);
      this.addEventListener('pointerenter', () => { this.hovered = true; this.schedule(); }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, options);
      this.addEventListener('focusin', () => this.schedule(), options);
      this.addEventListener('focusout', () => queueMicrotask(() => { if (this.isConnected) this.schedule(); }), options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.addEventListener('touchstart', event => {
        this.touch = { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY };
      }, { ...options, passive: true });
      this.addEventListener('touchend', event => {
        if (!this.touch) return;
        const dx = event.changedTouches[0].clientX - this.touch.x;
        const dy = event.changedTouches[0].clientY - this.touch.y;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          this.show(this.index + (dx < 0 ? 1 : -1));
          this.schedule();
        }
        this.touch = null;
      }, { ...options, passive: true });
      window.addEventListener('pf:demand', event => this.addRestocks(event.detail), options);
      this.schedule();
    }
    async addRestocks(products) {
      if (this.restocksLoaded) return;
      this.restocksLoaded = true;
      const root = window.Shopify?.routes?.root || '/';
      const candidates = products.filter(p => p.backInStock && p.available).sort((a, b) => Date.parse(b.restockedAt) - Date.parse(a.restockedAt) || b.count - a.count).slice(0, 5);
      const slides = [];
      for (const item of candidates) {
        try {
          const response = await fetch(`${root}products/${encodeURIComponent(item.handle)}.js`, { signal: AbortSignal.timeout(10000) });
          if (!response.ok) continue;
          const product = await response.json();
          if (!product.available || String(product.id) !== item.productId || !product.featured_image) continue;
          const slide = this.querySelector('[data-restock-template]').content.firstElementChild.cloneNode(true);
          const url = `${root}products/${encodeURIComponent(item.handle)}`;
          slide.dataset.handle = item.handle;
          slide.querySelector('h2').textContent = product.title;
          slide.setAttribute('aria-label', `Back In Stock: ${product.title}`);
          const currency = window.Shopify?.currency?.active || 'PKR';
          slide.querySelector('.pf-slide-price').textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency, minimumFractionDigits: product.price % 100 ? 2 : 0 }).formatToParts(product.price / 100).map(part => part.type === 'currency' && currency === 'PKR' ? 'Rs.' : part.value).join('');
          slide.querySelectorAll('a').forEach(a => { a.href = url; });
          const img = slide.querySelector('img'); img.src = product.featured_image; img.alt = product.title;
          slides.push(slide);
        } catch { /* Keep the verified native slideshow if the availability check fails. */ }
      }
      if (!this.isConnected || !slides.length) return;
      const handles = new Set(slides.map(s => s.dataset.handle));
      this.slides.forEach(s => { const href = s.querySelector('a')?.getAttribute('href') || ''; if ([...handles].some(h => href.split('?')[0].endsWith('/products/' + h))) s.remove(); });
      this.prepend(...slides);
      [...this.querySelectorAll('.pf-product-slide')].slice(5).forEach(slide => slide.remove());
      this.slides = [...this.querySelectorAll('.pf-product-slide')];
      this.controls.querySelectorAll('[data-slide]').forEach(dot => dot.remove());
      this.slides.forEach((slide, i) => { const dot = document.createElement('button'); dot.type = 'button'; dot.dataset.slide = i; dot.setAttribute('aria-label', `Show Product ${i + 1}`); dot.append(document.createElement('span')); this.controls.insertBefore(dot, this.pauseButton); });
      this.controls.hidden = this.slides.length < 2;
      this.querySelector('.pf-hero-arrows').hidden = this.slides.length < 2;
      this.show(0); this.schedule();
    }
    show(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; });
      this.querySelectorAll('[data-slide]').forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
    updatePause() {
      this.pauseButton.textContent = this.paused ? 'Play' : 'Pause';
      this.pauseButton.setAttribute('aria-label', this.paused ? 'Play Slideshow' : 'Pause Slideshow');
    }
    schedule() {
      clearTimeout(this.timer);
      if (this.slides.length < 2 || this.paused || this.hovered || document.hidden || this.contains(document.activeElement)) return;
      this.timer = setTimeout(() => { this.show(this.index + 1); this.schedule(); }, 6000);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.abort?.abort();
    }
  });
}
